/* eslint-disable no-unreachable */
import { useMemo } from "react";
import { InstrumentTypeResponse } from "../../codegen-api";
import { COMPONENTS } from "../../constants/design/spacing";
import {
  IOptionMarket,
  IPerpsMarket,
} from "../../contexts/MarketInstrumentContext/useGetMarkets";
import useIndexWSS from "../../hooks/wss/useIndexWSS";
import useMarkPriceWSS from "../../hooks/wss/useMarkPriceWSS";
import useTickersWSS from "../../hooks/wss/tickers/useTickersWSS";
import { AssetResponse } from "../../utils/asset";
import { instrumentLabel } from "../../utils/instrumentLabel";
import { firstKnownPrice } from "../../utils/markGuards";
import { BaseModal } from "../BaseModal";
import OptionsTradeForm from "../TradeForm/OptionsTradeForm";
import PerpsTradeForm, {
  IPerpsFormDefaultValues,
} from "../TradeForm/PerpsTradeForm";
import Price from "../TradingHeader/Price";
import { ModalTitlePrice, TradeFormContainer } from "./style";

export type IMobileTradeFormModalProps = {
  show?: boolean;
  onHide: () => void;
} & (
  | {
      instrumentType: typeof InstrumentTypeResponse.Option;
      selectedInstrument?: IOptionMarket;
      defaultValues?: undefined;
    }
  | {
      instrumentType: typeof InstrumentTypeResponse.Perpetual;
      selectedInstrument?: IPerpsMarket;
      defaultValues?: IPerpsFormDefaultValues;
    }
  | {
      instrumentType: typeof InstrumentTypeResponse.Spot;
      selectedInstrument?: IPerpsMarket;
      defaultValues?: IPerpsFormDefaultValues;
    }
);

function MobileTradeFormModal({
  show,
  onHide,
  instrumentType,
  selectedInstrument,
  defaultValues,
}: IMobileTradeFormModalProps) {
  const asset = selectedInstrument?.underlying_asset as AssetResponse | undefined;
  const instrumentName = selectedInstrument?.instrument_name;
  const isPerp = instrumentType === InstrumentTypeResponse.Perpetual;
  const isSpot = instrumentType === InstrumentTypeResponse.Spot;

  // The sheet shows THIS instrument's own mark (A2/A23). It used to show the
  // perp index for every vertical, so the spot sheet read "BTC-USD $83,794" —
  // the perp's name and price — while spot traded near $78k.
  // `markprice:` exists for perpetuals only (the backend rejects SPOT), so a
  // spot market reads its mark from its own ticker, as the header does.
  const markTickers = useMemo(
    () =>
      isPerp && instrumentName && asset
        ? { [instrumentName]: { asset, derivative: InstrumentTypeResponse.Perpetual } }
        : {},
    [asset, instrumentName, isPerp]
  );
  const { instrumentMark } = useMarkPriceWSS(markTickers);
  const { instrumentTicker } = useTickersWSS(
    isSpot ? asset : undefined,
    isSpot ? InstrumentTypeResponse.Spot : undefined
  );
  // Spot has no index stream: subscribing `index:<asset>` would read the perp's.
  const { index } = useIndexWSS(isPerp ? asset : undefined);

  const livePrice = isSpot
    ? instrumentTicker[instrumentName ?? ""]?.mark?.price
    : instrumentMark[instrumentName ?? ""]?.mark_price;
  const price = firstKnownPrice(
    livePrice,
    selectedInstrument?.mark_price,
    isPerp ? index?.price : undefined
  );

  return (
    <BaseModal
      noContentPadding
      show={show}
      onHide={onHide}
      title={
        <ModalTitlePrice data-testid="mobile-trade-sheet-title">
          <span>{instrumentLabel(asset, instrumentType)}</span>
          <Price
            currentPrice={price === undefined ? undefined : Number(price)}
            asset={selectedInstrument?.underlying_asset}
            type="priceOnly"
          />
        </ModalTitlePrice>
      }
      titleStyle={{
        flex: 1,
        padding: 0,
        marginLeft: COMPONENTS.modalHeaderHeight,
      }}
    >
      <TradeFormContainer>
        {instrumentType === InstrumentTypeResponse.Option && (
          <OptionsTradeForm
            mobileMode
            selectedInstrument={selectedInstrument}
            onClose={onHide}
          />
        )}
        {instrumentType === InstrumentTypeResponse.Perpetual && (
          <PerpsTradeForm
            mobileMode
            defaultValues={defaultValues}
            perpInstrument={selectedInstrument}
            tradingType="perp"
            onClose={onHide}
          />
        )}
        {instrumentType === InstrumentTypeResponse.Spot && (
          <PerpsTradeForm
            mobileMode
            defaultValues={defaultValues}
            perpInstrument={selectedInstrument}
            tradingType="spot"
            onClose={onHide}
          />
        )}
      </TradeFormContainer>
    </BaseModal>
  );
}

export default MobileTradeFormModal;
